import React, { useEffect, useState } from "react";
import { Card, Table, Tag, Select, Space, Input, DatePicker, Button, Tooltip } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as dayjs from "dayjs";
import { formatPriceByCurrency, formatPriceUsd } from "../../../../functions/formatters";
import { findDuplicatePaymentMatches } from "../../../../functions/duplicatePayments";
import urls from "../../../../urls";
import { usePurchaseOrderInvoices, usePurchaseOrderPayments } from "../../../../data/hooks";

const { RangePicker } = DatePicker;

const invoiceStatusColors = {
  draft: "default",
  submitted: "processing",
  approved: "blue",
  partially_paid: "orange",
  paid: "green",
  disputed: "red",
};

const invoiceStatusLabels = {
  draft: "Concept",
  submitted: "Ingediend",
  approved: "Goedgekeurd",
  partially_paid: "Deels betaald",
  paid: "Betaald",
  disputed: "Betwist",
};

const PurchaseOrderInvoicesList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [supplierFilter, setSupplierFilter] = useState(searchParams.get("supplier") || null);
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || null);
  const [typeFilter, setTypeFilter] = useState(searchParams.get("type") || null);
  const [searchText, setSearchText] = useState(searchParams.get("q") || "");
  const [dateRange, setDateRange] = useState(null);
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get("page")) || 1);

  // Load filters from localStorage on component mount
  useEffect(() => {
    try {
      const savedFilters = localStorage.getItem("purchaseOrderInvoicesFilters");
      if (savedFilters) {
        const filters = JSON.parse(savedFilters);
        if (filters.supplierFilter) setSupplierFilter(filters.supplierFilter);
        if (filters.statusFilter) setStatusFilter(filters.statusFilter);
        if (filters.typeFilter) setTypeFilter(filters.typeFilter);
        if (filters.searchText) setSearchText(filters.searchText);
        if (filters.currentPage) setCurrentPage(filters.currentPage);
      }
    } catch (error) {
      console.warn("Failed to load invoice filters from localStorage:", error);
    }
  }, []);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(
        "purchaseOrderInvoicesFilters",
        JSON.stringify({ supplierFilter, statusFilter, typeFilter, searchText, currentPage }),
      );
    } catch (error) {
      console.warn("Failed to save invoice filters to localStorage:", error);
    }
  }, [supplierFilter, statusFilter, typeFilter, searchText, currentPage]);

  // Use hook to fetch invoices
  const { invoices, loading } = usePurchaseOrderInvoices();

  // Own + linked-proforma payments, for the issue indicator's payments-drift check
  const { payments: allPayments } = usePurchaseOrderPayments({ invoiceIds: invoices.map((inv) => inv.id) });

  const updateSearchParams = (updates) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) params.set(key, value);
      else params.delete(key);
    });
    setSearchParams(params, { replace: true });
  };

  // Get unique suppliers for filter, sorted alphabetically
  const suppliers = [
    ...new Map(invoices.map((inv) => [inv.supplierId, { id: inv.supplierId, name: inv.supplierName }])).values(),
  ].sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  // Apply filters
  const filteredInvoices = invoices.filter((inv) => {
    if (supplierFilter && inv.supplierId !== supplierFilter) return false;
    if (statusFilter && (inv.status || "submitted") !== statusFilter) return false;
    if (typeFilter && inv.type !== typeFilter) return false;
    if (searchText) {
      const search = searchText.toLowerCase();
      const matchesNumber = inv.invoiceNumber?.toLowerCase().includes(search);
      const matchesShipment = inv.shipmentNumber?.toLowerCase().includes(search);
      const matchesOrder = inv.purchaseOrderNumber?.toLowerCase().includes(search);
      if (!matchesNumber && !matchesShipment && !matchesOrder) return false;
    }
    if (dateRange && dateRange[0] && dateRange[1]) {
      const invoiceDate = inv.invoiceDate ? dayjs.unix(inv.invoiceDate) : null;
      if (!invoiceDate) return false;
      if (invoiceDate.isBefore(dateRange[0], "day") || invoiceDate.isAfter(dateRange[1], "day")) {
        return false;
      }
    }
    return true;
  });

  // Summary stats
  const totalAmount = filteredInvoices.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);
  const totalPaid = filteredInvoices.reduce((sum, inv) => sum + (inv.totalPaid || 0), 0);
  const totalOutstanding = filteredInvoices.reduce((sum, inv) => sum + (inv.amountDue ?? inv.totalAmount ?? 0), 0);
  const syncedCount = filteredInvoices.filter((inv) => inv.exactPurchaseInvoiceId).length;

  console.log("totalAmount", totalAmount);
  console.log("totalPaid", totalPaid);
  console.log("totalOutstanding", totalOutstanding);

  const invoicesById = new Map(invoices.map((inv) => [inv.id, inv]));

  // Flags rows where stored totals don't add up, so discrepancies can be spotted from the overview
  // without opening every invoice. Payments-drift only sums this invoice's own + linked-proforma
  // payments (not legacy purchaseOrders.payments) - an invoice still settled the legacy way will
  // show a drift here even though its stored totalPaid is correct.
  const getInvoiceIssues = (record) => {
    const round2 = (n) => Math.round((n || 0) * 100) / 100;
    const totalAmount = round2(record.totalAmount);
    const totalPaid = round2(record.totalPaid);
    const amountDue = round2(record.amountDue ?? record.totalAmount ?? 0);

    const issues = [];
    // Proforma invoices are created as a lump totalAmount with no line items (subtotals are
    // always 0 by design), so this check only makes sense for final invoices.
    if (record.type === "final") {
      const linesTotal = round2(
        (record.subtotalProducts || 0) + (record.subtotalAdditionalCosts || 0) + (record.subtotalDiscounts || 0),
      );
      if (Math.abs(totalAmount - linesTotal) >= 0.01) {
        issues.push(
          `Totaalbedrag (${formatPriceByCurrency(totalAmount, record.currency)}) komt niet overeen met som van regels (${formatPriceByCurrency(linesTotal, record.currency)})`,
        );
      }
    }
    if (Math.abs(amountDue - round2(totalAmount - totalPaid)) >= 0.01) {
      issues.push(
        `Openstaand (${formatPriceByCurrency(amountDue, record.currency)}) komt niet overeen met totaal minus betaald (${formatPriceByCurrency(round2(totalAmount - totalPaid), record.currency)})`,
      );
    }

    const relevantPayments = allPayments.filter(
      (p) =>
        p.invoiceId === record.id ||
        (record.type === "final" && record.linkedProformaInvoiceIds?.includes(p.invoiceId)),
    );
    const paymentsSum = round2(relevantPayments.reduce((sum, p) => sum + (p.amount || 0), 0));
    if (Math.abs(paymentsSum - totalPaid) >= 0.01) {
      issues.push(
        `Som betalingen (${formatPriceByCurrency(paymentsSum, record.currency)}) komt niet overeen met betaald (${formatPriceByCurrency(totalPaid, record.currency)})`,
      );
    }

    // A payment without its own exchangeRate is silently excluded from the weighted
    // exchange-rate calculation on the shipment's "Verwerk ontvangst" screen - most often
    // seen on payments migrated from the old order-level system, which never had one.
    if ((record.currency || "USD") !== "EUR") {
      const paymentsMissingRate = relevantPayments.filter((p) => !p.exchangeRate);
      if (paymentsMissingRate.length > 0) {
        issues.push(
          `${paymentsMissingRate.length} betaling${paymentsMissingRate.length > 1 ? "en" : ""} zonder wisselkoers - telt niet mee in de gewogen-koers-berekening`,
        );
      }
    }

    // Same-supplier payments (any of their invoices, including this one) that possibly
    // duplicate one of this invoice's own payments - e.g. the same deposit entered on both
    // the proforma and the final invoice.
    const ownPayments = allPayments.filter((p) => p.invoiceId === record.id);
    const supplierPayments = allPayments.filter((p) => invoicesById.get(p.invoiceId)?.supplierId === record.supplierId);
    const duplicateMatches = ownPayments.flatMap((p) => findDuplicatePaymentMatches(p, supplierPayments, invoicesById));
    if (duplicateMatches.length > 0) {
      const sameInvoiceDuplicate = duplicateMatches.some((m) => m.invoice.id === record.id);
      const otherInvoiceLabels = [
        ...new Set(
          duplicateMatches.filter((m) => m.invoice.id !== record.id).map((m) => m.invoice.invoiceNumber || m.invoice.id),
        ),
      ];
      const parts = [];
      if (sameInvoiceDuplicate) parts.push("twee keer dezelfde betaling op deze factuur");
      if (otherInvoiceLabels.length > 0) parts.push(`ook geregistreerd bij factuur: ${otherInvoiceLabels.join(", ")}`);
      issues.push(`Mogelijk dubbele betaling - ${parts.join("; ")}`);
    }

    return issues;
  };

  const columns = [
    {
      title: t("created_at"),
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: (a, b) => (a.createdAt || 0) - (b.createdAt || 0),
      render: (sec) => (sec ? dayjs.unix(sec).format("DD-MM-YYYY HH:mm") : "-"),
    },
    {
      title: "",
      key: "issue",
      width: 50,
      render: (_, record) => {
        const issues = getInvoiceIssues(record);
        if (issues.length === 0) return null;
        return (
          <Tooltip
            title={
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {issues.map((issue, i) => (
                  <li key={i}>{issue}</li>
                ))}
              </ul>
            }
          >
            <WarningOutlined style={{ color: "#fa8c16", fontSize: 16, cursor: "help" }} />
          </Tooltip>
        );
      },
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type) =>
        type === "proforma" ? <Tag color="orange">Pro forma</Tag> : <Tag color="blue">Definitief</Tag>,
    },
    {
      title: t("invoice.invoice_number"),
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
      sorter: (a, b) => (a.invoiceNumber || "").localeCompare(b.invoiceNumber || ""),
    },
    {
      title: t("supplier.supplier"),
      dataIndex: "supplierName",
      key: "supplierName",
      render: (name) => name || "-",
    },
    {
      title: "Referentie",
      key: "reference",
      render: (_, record) => {
        if (record.type === "proforma") {
          const poTag =
            record.purchaseOrderNumber || record.purchaseOrderId ? (
              <Tag
                color="geekblue"
                style={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`${urls.purchaseOrdersList}/${record.purchaseOrderId}`);
                }}
              >
                {record.purchaseOrderNumber || `${record.purchaseOrderId.substring(0, 8)}...`}
              </Tag>
            ) : null;
          const linkTag = record.linkedFinalInvoiceId ? (
            (() => {
              const finalInvoice = invoices.find((inv) => inv.id === record.linkedFinalInvoiceId);
              return (
                <Tag
                  color="green"
                  style={{ cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`${urls.purchaseOrderInvoiceDetail}${record.linkedFinalInvoiceId}`);
                  }}
                >
                  Verrekend: {finalInvoice?.invoiceNumber || record.linkedFinalInvoiceId.substring(0, 8)}
                </Tag>
              );
            })()
          ) : (
            <Tag>Nog niet verrekend</Tag>
          );
          return (
            <Space direction="vertical" size={2}>
              {poTag}
              {linkTag}
            </Space>
          );
        }
        // Final invoice: shipment reference plus any settled proforma invoices
        const shipmentTag = record.shipmentNumber ? (
          <Tag
            color="blue"
            style={{ cursor: "pointer" }}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`${urls.purchaseOrderShipmentDetail}${record.shipmentId}`);
            }}
          >
            {record.shipmentNumber}
          </Tag>
        ) : null;
        const count = record.linkedProformaInvoiceIds?.length || 0;
        const linkTag =
          count > 0 ? (
            <Tag color="green">
              {count} proforma{count > 1 ? "'s" : ""} verrekend
            </Tag>
          ) : (
            <Tag>Geen aanbetaling gekoppeld</Tag>
          );
        return (
          <Space direction="vertical" size={2}>
            {shipmentTag}
            {linkTag}
          </Space>
        );
      },
    },
    {
      title: t("invoice.invoice_date"),
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      sorter: (a, b) => (a.invoiceDate || 0) - (b.invoiceDate || 0),
      render: (sec) => (sec ? dayjs.unix(sec).format("DD-MM-YYYY") : "-"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={invoiceStatusColors[status] || "default"}>
          {invoiceStatusLabels[status] || status || "Ingediend"}
        </Tag>
      ),
    },
    {
      title: "Totaal / Betaald / Openstaand",
      key: "amounts",
      sorter: (a, b) => (a.totalAmount || 0) - (b.totalAmount || 0),
      render: (_, record) => {
        const due = record.amountDue ?? record.totalAmount ?? 0;
        return (
          <div style={{ whiteSpace: "nowrap", fontSize: 12, lineHeight: 1.5 }}>
            <div>{formatPriceByCurrency(record.totalAmount, record.currency)}</div>
            <div>Betaald: {formatPriceByCurrency(record.totalPaid || 0, record.currency)}</div>
            <div>
              Openstaand:{" "}
              {due > 0 ? (
                <span style={{ color: "#ff4d4f", fontWeight: "bold" }}>
                  {formatPriceByCurrency(due, record.currency)}
                </span>
              ) : (
                <span style={{ color: "#52c41a" }}>✓</span>
              )}
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <h2>{t("purchase_order_invoices")}</h2>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <Space>
            <Input.Search
              allowClear
              placeholder="Zoek op factuurnr / verzending..."
              style={{ width: 260 }}
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setCurrentPage(1);
                updateSearchParams({ q: e.target.value || null, page: null });
              }}
            />
            <Select
              allowClear
              placeholder={t("supplier.supplier")}
              style={{ minWidth: 200 }}
              value={supplierFilter}
              onChange={(v) => {
                setSupplierFilter(v);
                setCurrentPage(1);
                updateSearchParams({ supplier: v, page: null });
              }}
              options={suppliers.map((s) => ({ value: s.id, label: s.name || s.id }))}
            />
            <Select
              allowClear
              placeholder="Status"
              style={{ minWidth: 150 }}
              value={statusFilter}
              onChange={(v) => {
                setStatusFilter(v);
                setCurrentPage(1);
                updateSearchParams({ status: v, page: null });
              }}
              options={Object.entries(invoiceStatusLabels).map(([value, label]) => ({ value, label }))}
            />
            <Select
              allowClear
              placeholder="Type"
              style={{ minWidth: 150 }}
              value={typeFilter}
              onChange={(v) => {
                setTypeFilter(v);
                setCurrentPage(1);
                updateSearchParams({ type: v, page: null });
              }}
              options={[
                { value: "proforma", label: "Pro forma" },
                { value: "final", label: "Definitief" },
              ]}
            />
            <RangePicker format="DD-MM-YYYY" onChange={(dates) => setDateRange(dates)} />
          </Space>
        </div>

        {/* Summary bar */}
        <div
          style={{
            display: "flex",
            gap: 24,
            marginBottom: 16,
            padding: 12,
            backgroundColor: "#f0f5ff",
            borderRadius: 8,
          }}
        >
          <div>
            <span style={{ color: "#666" }}>Aantal facturen:</span> <strong>{filteredInvoices.length}</strong>
          </div>

          <div>
            <span style={{ color: "#666" }}>Totaalbedrag:</span>{" "}
            <strong style={{ color: "#1890ff" }}>{formatPriceUsd(totalAmount)}</strong>
          </div>
          <div>
            <span style={{ color: "#666" }}>Totaal betaald:</span>{" "}
            <strong style={{ color: "#52c41a" }}>{formatPriceUsd(totalPaid)}</strong>
          </div>
          <div>
            <span style={{ color: "#666" }}>Totaal openstaand:</span>{" "}
            <strong style={{ color: "#ff4d4f" }}>{formatPriceUsd(totalOutstanding)}</strong>
          </div>
        </div>

        <Table
          loading={loading}
          dataSource={filteredInvoices}
          columns={columns}
          rowKey="id"
          scroll={{ x: true }}
          onRow={(record) => ({
            onClick: () => navigate(`${urls.purchaseOrderInvoiceDetail}${record.id}`),
            style: { cursor: "pointer" },
          })}
          pagination={{
            current: currentPage,
            defaultPageSize: 20,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} van ${total}`,
            onChange: (page) => {
              setCurrentPage(page);
              updateSearchParams({ page: String(page) });
            },
          }}
        />
      </Card>
    </>
  );
};

export default PurchaseOrderInvoicesList;
